import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Typography, Checkbox, FormControlLabel, TextField, Autocomplete, Chip, Button, Skeleton} from '@mui/material'
import {Controller} from 'react-hook-form'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {clientApi} from '../../../../services'
import {useParams} from 'react-router-dom'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {parseError} from '../../../../utils/helpers'
import {SkeletonContainer, FieldSkeleton, ButtonSkeleton, ChipSkeleton} from '../../../../components'

// Headings and option labels are static; only the checked state and languages come from the API.
const CheckSkeleton = ({label}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 12, height: 42, paddingLeft: 12, marginRight: 16}}>
        <Skeleton variant="rounded" width={18} height={18} sx={{borderRadius: 0.5}} />
        <Typography variant="body1" color="text.disabled">
            {label}
        </Typography>
    </div>
)

const CheckGroupSkeleton = ({title, labels}) => (
    <>
        <Typography variant="inputLabel" color="text.secondary">
            {title}
        </Typography>
        <br />
        <div className="check">
            {labels.map(label => (
                <CheckSkeleton key={label} label={label} />
            ))}
        </div>
        <br />
    </>
)

const PreferencesSkeleton = () => (
    <SkeletonContainer>
        <CheckGroupSkeleton title="Pet Preferences" labels={['Has Dog', 'Has Cat', 'Has Pet Care Assistance']} />
        <CheckGroupSkeleton title="Gender Preferences" labels={['Man', 'Woman']} />
        <CheckGroupSkeleton title="Availability" labels={['Full-time', 'Part-time', 'Live-in', 'Hourly shifts']} />
        <Typography variant="inputLabel" color="text.secondary">
            Language Spoken
        </Typography>
        <FieldSkeleton label={false} />
        <div className="languange">
            <ChipSkeleton width={84} height={32} />
            <ChipSkeleton width={72} height={32} />
        </div>
        <br />
        <CheckGroupSkeleton title="Environment & Lifestyle" labels={['Smoker in Household']} />
        <br />
        <CheckGroupSkeleton title="Additional Skills for Caregiver" labels={['Driving', 'Wound Care', 'Physical Theraphy']} />
        <CheckGroupSkeleton title="Medical Equipment Assistance" labels={['Requires Hoyer List Assistance']} />
        <br />
        <br />
        <div className="actions">
            <ButtonSkeleton width={80} />
            <ButtonSkeleton width={140} />
        </div>
    </SkeletonContainer>
)

export default () => {
    const [selectedLanguages, setSelectedLanguages] = useState([])
    const {setLoading, setMessageBox} = useGlobalStates()
    const {clientId} = useParams()
    const [skeleton, setSkeleton] = useState(true)
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                hasDog: yup.boolean().default(false),
                hasCat: yup.boolean().default(false),
                hasPetCareAssistance: yup.boolean().default(false),
                man: yup.boolean().default(false),
                woman: yup.boolean().default(false),
                fullTime: yup.boolean().default(false),
                partTime: yup.boolean().default(false),
                liveIn: yup.boolean().default(false),
                hourlyShifts: yup.boolean().default(false),
                languages: yup.array().of(yup.string()),
                smokerInHoushold: yup.boolean().default(false),
                driving: yup.boolean().default(false),
                woundCare: yup.boolean().default(false),
                physicalTherapy: yup.boolean().default(false),
                requiresHoyerLiftAssistance: yup.boolean().default(false),
            }),
        ),
    })

    const languages = [
        {key: 'en', value: 'English'},
        {key: 'es', value: 'Spanish'},
        {key: 'fr', value: 'French'},
        {key: 'zh', value: 'Chinese'},
    ]

    useEffect(() => {
        reset(true)
    }, [])

    useEffect(() => {
        if (form.getValues('languages')) {
            selectedLanguages.length = 0
            form.getValues('languages').forEach(key => {
                selectedLanguages.push(languages.find(language => language.key == key))
            })
        }
    }, [])

    useEffect(() => {
        form.setValue(
            'languages',
            selectedLanguages.map(lang => lang.key),
        )
    }, [selectedLanguages])

    // The first load shows the skeleton; the Reset button refetches under the global spinner.
    const reset = (initial = false) => {
        const done = () => (initial ? setSkeleton(false) : setLoading(false))
        if (!initial) setLoading(true)
        clientApi
            .getClientPreferences(clientId)
            .then(({data: {data}}) => {
                done()
                form.setValue('languages', data?.languages)
                selectedLanguages.length = 0
                data?.languages?.forEach(key => {
                    selectedLanguages.push(languages.find(language => language.key == key))
                })
                form.setValue('hasDog', data?.has_dog)
                form.setValue('hasCat', data?.has_cat)
                form.setValue('hasPetCareAssistance', data?.has_pet_care_assistance)
                form.setValue('man', data?.prefers_male)
                form.setValue('woman', data?.prefers_female)
                form.setValue('fullTime', data?.needs_full_time)
                form.setValue('partTime', data?.needs_part_time)
                form.setValue('liveIn', data?.needs_live_in)
                form.setValue('hourlyShifts', data?.needs_hourly_shifts)
                form.setValue('smokerInHoushold', data?.smoker_in_household)
                form.setValue('driving', data?.requires_driving)
                form.setValue('woundCare', data?.requires_wound_care)
                form.setValue('physicalTherapy', data?.requires_physical_therapy)
                form.setValue('requiresHoyerLiftAssistance', data?.requires_hoyer_lift_assistance)
            })
            .catch(error => {
                done()
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const submit = async data => {
        setLoading(true)
        clientApi
            .updateClientPreferences(clientId, {
                languages: data?.languages,
                has_dog: data?.hasDog,
                has_cat: data?.hasCat,
                has_pet_care_assistance: data?.hasPetCareAssistance,
                prefers_male: data?.man,
                prefers_female: data?.woman,
                no_gender_preference: !data?.man && !data?.woman,
                needs_full_time: data?.fullTime,
                needs_part_time: data?.partTime,
                needs_live_in: data?.liveIn,
                needs_hourly_shifts: data?.hourlyShifts,
                smoker_in_household: data?.smokerInHoushold,
                requires_driving: data?.driving,
                requires_wound_care: data?.woundCare,
                requires_physical_therapy: data?.physicalTherapy,
                requires_hoyer_lift_assistance: data?.requiresHoyerLiftAssistance,
            })
            .then(() => {
                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Client Informations Updated',
                    description: 'Client Preferences have been successfully updated. All changes are now reflected in the system.',
                })
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <>
            {skeleton && <PreferencesSkeleton />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="inputLabel" color="text.secondary">
                    Pet Preferences
                </Typography>
                <br />
                <div className="check">
                    <Controller
                        {...form.register('hasDog')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Has Dog"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('hasCat')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Has Cat"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('hasPetCareAssistance')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Has Pet Care Assistance"
                            />
                        )}
                    />
                </div>
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Gender Preferences
                </Typography>
                <br />
                <div className="check">
                    <Controller
                        {...form.register('man')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Man"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('woman')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Woman"
                            />
                        )}
                    />
                </div>
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Availability
                </Typography>
                <br />
                <div className="check">
                    <Controller
                        {...form.register('fullTime')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Full-time"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('partTime')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Part-time"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('liveIn')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Live-in"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('hourlyShifts')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Hourly shifts"
                            />
                        )}
                    />
                </div>
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Language Spoken
                </Typography>
                <Autocomplete
                    multiple
                    options={languages}
                    value={selectedLanguages}
                    onChange={(e, newVal) => setSelectedLanguages(newVal)}
                    isOptionEqualToValue={(opt, val) => opt.key === val.key}
                    getOptionLabel={opt => opt.value}
                    renderInput={params => <TextField {...params} placeholder="Add your languages" />}
                    slotProps={{chip: {sx: {display: 'none'}}}}
                />
                <div className="languange">
                    {selectedLanguages.map(item => (
                        <Chip
                            key={item.key}
                            label={item.value}
                            onDelete={() => setSelectedLanguages(prev => prev.filter(p => p.key !== item.key))}
                            variant="contained"
                            size="medium"
                        />
                    ))}
                </div>
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Environment & Lifestyle
                </Typography>
                <br />
                <Controller
                    {...form.register('smokerInHoushold')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Smoker in Household"
                        />
                    )}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Additional Skills for Caregiver
                </Typography>
                <br />
                <div className="check">
                    <Controller
                        {...form.register('driving')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Driving"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('woundCare')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Wound Care"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('physicalTherapy')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Physical Theraphy"
                            />
                        )}
                    />
                </div>
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Medical Equipment Assistance
                </Typography>
                <br />
                <Controller
                    {...form.register('requiresHoyerLiftAssistance')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Requires Hoyer List Assistance"
                        />
                    )}
                />
                <br />
                <br />
                <br />
                <div className="actions">
                    <Button variant="outlined" onClick={() => reset()}>
                        Reset
                    </Button>
                    <Button variant="contained" onClick={form.handleSubmit(submit)}>
                        Save Changes
                    </Button>
                </div>
            </div>
        </>
    )
}
